export const CommercialPlanKey = {
  Mem: "mem_free_v1",
  MemPlus: "mem_plus_monthly_v1",
  MemPro: "mem_pro_monthly_v1",
  MemPro2x: "mem_pro_2x_monthly_v1",
  MemPro5x: "mem_pro_5x_monthly_v1",
  MemPro10x: "mem_pro_10x_monthly_v1",
  MemPro20x: "mem_pro_20x_monthly_v1",
} as const;
export type CommercialPlanKey = (typeof CommercialPlanKey)[keyof typeof CommercialPlanKey];

export const CommercialMeterKey = {
  UserRequests: "user_requests",
  NewNotes: "new_notes",
  ActiveNotes: "active_notes",
  OpenTasksProjects: "open_tasks_projects",
  ActiveCustomRoutines: "active_custom_routines",
  RoutineExecutions: "routine_executions",
  ActiveConnections: "active_connections",
  VoiceModeHours: "voice_mode_hours",
} as const;
export type CommercialMeterKey = (typeof CommercialMeterKey)[keyof typeof CommercialMeterKey];

export const CommercialMeterKind = {
  Cumulative: "cumulative",
  Gauge: "gauge",
} as const;
export type CommercialMeterKind = (typeof CommercialMeterKind)[keyof typeof CommercialMeterKind];

export const CommercialUsageStatus = {
  Available: "available",
  Nearing: "nearing",
  AtLimit: "at_limit",
} as const;
export type CommercialUsageStatus =
  (typeof CommercialUsageStatus)[keyof typeof CommercialUsageStatus];

export type CommercialAllowances = Record<CommercialMeterKey, number>;

export interface CommercialPlanPresentationDefinition {
  key: CommercialPlanKey;
  displayName: string;
  shortName: string;
  monthlyPriceCents: number;
  position: number;
  allowances: CommercialAllowances;
  supportsPaidExtraUsage: boolean;
}

export interface CommercialPlanDefinition extends CommercialPlanPresentationDefinition {
  checkoutAvailable: boolean;
}

export interface CommercialExtraUsageBlockDefinition {
  key: string;
  displayName: string;
  monthlyPriceCents: number;
  allowances: CommercialAllowances;
}

export interface CommercialCatalogSnapshot {
  generation: string;
  plans: readonly CommercialPlanDefinition[];
  extraUsageBlock: CommercialExtraUsageBlockDefinition;
  extraUsageAvailable: boolean;
  planManagementAvailable: boolean;
  usageEnforcementEnabled: boolean;
}

export interface CommercialUsageMeterSnapshot {
  key: CommercialMeterKey;
  kind: CommercialMeterKind;
  allowance: number | null;
  used: number;
  current?: number | null;
  peak?: number | null;
  status: CommercialUsageStatus;
  resetAt?: string | null;
}

export interface CommercialCurrentPlanSnapshot {
  key: string;
  displayName: string;
  monthlyPriceCents: number | null;
  currency: "usd";
  isLegacy: boolean;
}

export interface CommercialBillingPeriodSnapshot {
  startsAt: string;
  endsAt: string;
}

export const CommercialExtraUsageMode = {
  PauseAndAsk: "pause_and_ask",
  AutoExpand: "auto_expand",
} as const;
export type CommercialExtraUsageMode =
  (typeof CommercialExtraUsageMode)[keyof typeof CommercialExtraUsageMode];

export interface CommercialExtraUsageSnapshot {
  available: boolean;
  priceCents: number;
  complimentaryBlocks: number;
  paidBlocks: number;
  mode: CommercialExtraUsageMode;
  maxPaidBlocks: number | null;
}

export const CommercialPendingPlanChangeStatus = {
  PendingPayment: "pending_payment",
  Scheduled: "scheduled",
} as const;
export type CommercialPendingPlanChangeStatus =
  (typeof CommercialPendingPlanChangeStatus)[keyof typeof CommercialPendingPlanChangeStatus];

export interface CommercialPendingPlanChangeSnapshot {
  destinationPlanKey: CommercialPlanKey | null;
  paidExtraUsageBlocks: number | null;
  effectiveAt: string;
  status: CommercialPendingPlanChangeStatus;
}

export const CommercialAllowanceKind = {
  Limited: "limited",
  Unlimited: "unlimited",
  Unknown: "unknown",
} as const;
export type CommercialAllowanceKind =
  (typeof CommercialAllowanceKind)[keyof typeof CommercialAllowanceKind];

export const CommercialPlanChangeTiming = {
  Immediate: "immediate",
  Renewal: "renewal",
  Unchanged: "unchanged",
} as const;
export type CommercialPlanChangeTiming =
  (typeof CommercialPlanChangeTiming)[keyof typeof CommercialPlanChangeTiming];

export const CommercialPlanChangeStatus = {
  Applied: "applied",
  PendingPayment: "pending_payment",
  Scheduled: "scheduled",
  Unchanged: "unchanged",
} as const;
export type CommercialPlanChangeStatus =
  (typeof CommercialPlanChangeStatus)[keyof typeof CommercialPlanChangeStatus];

export interface CommercialAllowanceComparison {
  currentAmount: number | null;
  currentKind: CommercialAllowanceKind;
  isReduction: boolean;
  meterKey: CommercialMeterKey;
  targetAmount: number;
}

export interface CommercialPlanChangePreview {
  endsTrial?: boolean;
  nextBillingAt?: string | null;
  allowanceComparisons: CommercialAllowanceComparison[];
  amountDueNowCents: number;
  currency: string | null;
  currentPaidExtraUsageBlocks: number;
  currentPlanKey: string;
  currentRecurringAmountCents: number | null;
  effectiveAt: string;
  isLegacyMigration: boolean;
  oneWayLegacyExit: boolean;
  providerInvoiceTotalCents: number | null;
  requiredMigrationConsentVersion: string | null;
  targetPaidExtraUsageBlocks: number;
  targetPlanKey: CommercialPlanKey;
  targetRecurringAmountCents: number;
  timing: CommercialPlanChangeTiming;
}

export interface CommercialPlanChangeResult {
  preview: CommercialPlanChangePreview;
  providerSubscriptionId: string;
  status: CommercialPlanChangeStatus;
}

export const CommercialUsageRecoveryAction = {
  ReduceUsage: "reduce_usage",
  RetryAfterReset: "retry_after_reset",
  ReviewExtraUsage: "review_extra_usage",
  ReviewPlans: "review_plans",
} as const;
export type CommercialUsageRecoveryAction =
  (typeof CommercialUsageRecoveryAction)[keyof typeof CommercialUsageRecoveryAction];

export interface CommercialUsageLimitRejection {
  accepted: boolean;
  code: "COMMERCIAL_USAGE_LIMIT_REACHED";
  meterKey: CommercialMeterKey;
  recoveryActions: CommercialUsageRecoveryAction[];
}

export const CommercialMigrationState = {
  LegacyShadow: "legacy_shadow",
  LegacyEligible: "legacy_eligible",
  LegacyOffered: "legacy_offered",
  LegacyDeclined: "legacy_declined",
  MigrationScheduled: "migration_scheduled",
  MigrationFailedPreserveLegacy: "migration_failed_preserve_legacy",
  NewCatalogActive: "new_catalog_active",
} as const;
export type CommercialMigrationState =
  (typeof CommercialMigrationState)[keyof typeof CommercialMigrationState];

export interface CommercialMigrationSnapshot {
  state: CommercialMigrationState;
  recommendedPlanKey?: CommercialPlanKey | null;
  recommendedPaidExtraUsageBlocks?: number | null;
  limitingMeterKey?: CommercialMeterKey | null;
  sourcePlanDisplayName?: string | null;
  sourceMonthlyPriceCents?: number | null;
  changedAllowances?: CommercialMigrationAllowanceChange[];
  eligible: boolean;
  canReturn: boolean;
}

export interface CommercialMigrationAllowanceChange {
  meterKey: CommercialMeterKey;
  currentDisplay: string;
  newAllowance: number;
  changeKind: string;
}

export interface CommercialPlanSnapshot {
  isTrial?: boolean;
  catalogGeneration: string;
  currentPlan: CommercialCurrentPlanSnapshot;
  memProTrialEligible?: boolean;
  period: CommercialBillingPeriodSnapshot;
  meters: Record<CommercialMeterKey, CommercialUsageMeterSnapshot>;
  blockPolicy?: CommercialExtraUsageSnapshot | null;
  pausedConnectionIds: readonly string[];
  pausedWorkingMemoryItems: readonly {
    id: string;
    kind: "assistant_task" | "assistant_project";
  }[];
  pendingChange?: CommercialPendingPlanChangeSnapshot | null;
  migration?: CommercialMigrationSnapshot | null;
}
